import { Droppable } from "@hello-pangea/dnd";
import { useTranslation } from "react-i18next";

export const OUTCOME_DROP_PREFIX = "outcome:";
export const ACTION_MOVE_TO = "action:MOVE_TO";

export type OutcomeDropId = "DELETED" | "LOST" | "WON";

const ZONES: Array<{
  id: string;
  labelKey: string;
  defaultLabel: string;
  textClass: string;
  activeClass: string;
}> = [
  {
    id: `${OUTCOME_DROP_PREFIX}DELETED`,
    labelKey: "project_drop_delete",
    defaultLabel: "DELETE",
    textClass: "text-[var(--p-color-text-secondary)]",
    activeClass:
      "border-[var(--p-color-border-strong)] bg-[var(--p-color-bg-surface-tertiary)]",
  },
  {
    id: `${OUTCOME_DROP_PREFIX}LOST`,
    labelKey: "project_drop_lost",
    defaultLabel: "LOST",
    textClass: "text-[var(--p-color-text-critical)]",
    activeClass:
      "border-[var(--p-color-border-critical)] bg-[var(--p-color-bg-surface-critical)]",
  },
  {
    id: `${OUTCOME_DROP_PREFIX}WON`,
    labelKey: "project_drop_won",
    defaultLabel: "WON",
    textClass: "text-[var(--p-color-text-success)]",
    activeClass:
      "border-[var(--p-color-border-success)] bg-[var(--p-color-bg-surface-success)]",
  },
  {
    id: ACTION_MOVE_TO,
    labelKey: "project_drop_move_to",
    defaultLabel: "MOVE TO",
    textClass: "text-[var(--p-color-text-secondary)]",
    activeClass:
      "border-[var(--p-color-border-strong)] bg-[var(--p-color-bg-surface-tertiary)]",
  },
];

interface OutcomeDropBarProps {
  /** Visual only — Droppables stay mounted + sized so @hello-pangea/dnd can hit-test. */
  visible: boolean;
}

/**
 * Pipedrive-style bottom drop targets.
 *
 * On a phone the zones form two rows of two, so every label fits. The bar sits at
 * the bottom of the board, which already ends above the phone bottom bar.
 *
 * IMPORTANT: Droppables are always mounted with a real height. Collapsing them
 * to max-h-0 / isDropDisabled during drag-start prevents drops from registering.
 * We only toggle opacity / pointer-events for show/hide.
 */
export default function OutcomeDropBar({ visible }: OutcomeDropBarProps) {
  const { t } = useTranslation();

  return (
    <div
      className={`absolute inset-x-0 bottom-0 z-30 bg-[var(--p-color-bg-surface-secondary)]/95 px-4 py-3 max-md:px-3 max-md:py-2 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] backdrop-blur-sm transition-opacity duration-150 ${
        visible ? "opacity-100" : "pointer-events-none opacity-0"
      }`}
      aria-hidden={!visible}
    >
      <div className="grid grid-cols-4 gap-3 max-md:grid-cols-2 max-md:gap-2">
        {ZONES.map((zone) => (
          <Droppable key={zone.id} droppableId={zone.id} type="CARD">
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`relative h-[76px] max-md:h-14 overflow-hidden rounded-xl border-2 border-dashed transition-colors ${
                  snapshot.isDraggingOver
                    ? zone.activeClass
                    : "border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)]"
                }`}
              >
                {/* Label stays centered; DnD placeholder must not push it. */}
                <span
                  className={`pointer-events-none absolute inset-0 flex items-center justify-center text-sm font-bold tracking-wide ${zone.textClass}`}
                >
                  {t(zone.labelKey, { defaultValue: zone.defaultLabel })}
                </span>
                {/* Required by @hello-pangea/dnd; keep out of layout. */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 overflow-hidden opacity-0"
                >
                  {provided.placeholder}
                </div>
              </div>
            )}
          </Droppable>
        ))}
      </div>
    </div>
  );
}

export function parseOutcomeDropId(droppableId: string): OutcomeDropId | null {
  if (!droppableId.startsWith(OUTCOME_DROP_PREFIX)) return null;
  const value = droppableId.slice(OUTCOME_DROP_PREFIX.length);
  if (value === "DELETED" || value === "LOST" || value === "WON") return value;
  return null;
}
